import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UsersThree, UserPlus, X, Heart } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Looking For Squad panel. Lives inside the game lobby. Lets the current user
 * mark themselves as available, see a live list of other LFS players for the
 * same game, and send a squad invite to any of them.
 */
export const LFSPanel = ({ game, user, disabled, onFavouritesChanged }) => {
  const [isLooking, setIsLooking] = useState(false);
  const [players, setPlayers] = useState([]);
  const [sendingInviteTo, setSendingInviteTo] = useState(null);
  const [friendIds, setFriendIds] = useState(new Set());
  const [savingFavourite, setSavingFavourite] = useState(null);
  const pollRef = useRef(null);

  const fetchPlayers = useCallback(async () => {
    if (!game?.id) return;
    try {
      const { data } = await axios.get(`${API}/lfs/${encodeURIComponent(game.id)}`);
      setPlayers(data.players || []);
    } catch (e) {
      logger.error('Failed to fetch LFS list', e);
    }
  }, [game?.id]);

  const fetchFriendIds = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/friends`);
      setFriendIds(new Set((data.friends || []).map((f) => f.user_id)));
    } catch (e) {
      /* non-fatal */
    }
  }, []);

  // Polling keeps the list fresh without needing another WS channel
  useEffect(() => {
    if (!game?.id) return;
    fetchPlayers();
    fetchFriendIds();
    pollRef.current = setInterval(fetchPlayers, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [game?.id, fetchPlayers, fetchFriendIds]);

  // Leave the queue when the user navigates away
  useEffect(() => {
    return () => {
      if (isLooking && game?.id) {
        axios
          .post(`${API}/lfs/leave`, { game_type: game.id })
          .catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLooking = async () => {
    if (!game?.id) return;
    try {
      if (isLooking) {
        await axios.post(`${API}/lfs/leave`, { game_type: game.id });
        setIsLooking(false);
        toast.success('Removed from squad queue');
      } else {
        await axios.post(`${API}/lfs/join`, { game_type: game.id });
        setIsLooking(true);
        toast.success(`Marked as looking for squad in ${game.name}`);
        fetchPlayers();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update LFS status');
    }
  };

  const sendInvite = async (toUser) => {
    setSendingInviteTo(toUser.user_id);
    try {
      await axios.post(`${API}/invites/send`, {
        to_user_id: toUser.user_id,
        game_type: game.id,
      });
      toast.success(`Invite sent to ${toUser.username}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSendingInviteTo(null);
    }
  };

  const toggleFavourite = async (lfsUser) => {
    setSavingFavourite(lfsUser.user_id);
    try {
      if (friendIds.has(lfsUser.user_id)) {
        await axios.post(`${API}/friends/remove`, { friend_user_id: lfsUser.user_id });
        setFriendIds((prev) => {
          const next = new Set(prev);
          next.delete(lfsUser.user_id);
          return next;
        });
      } else {
        await axios.post(`${API}/friends/add`, { friend_user_id: lfsUser.user_id });
        setFriendIds((prev) => new Set(prev).add(lfsUser.user_id));
        toast.success(`${lfsUser.username} saved as a favourite`);
      }
      if (onFavouritesChanged) onFavouritesChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update favourites');
    } finally {
      setSavingFavourite(null);
    }
  };

  return (
    <div
      className="bg-white border border-[#E4E4E7] rounded-lg p-5 w-full max-w-md mx-auto"
      data-testid="lfs-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <UsersThree size={20} weight="fill" className="text-[#0047FF]" />
          <h3 className="font-heading text-base font-bold text-[#09090B]">Looking for Squad</h3>
        </div>
        <Button
          onClick={toggleLooking}
          disabled={disabled}
          size="sm"
          className={
            isLooking
              ? 'bg-[#FF3333] hover:bg-[#E62A2A] text-white'
              : 'bg-[#0047FF] hover:bg-[#0037CC] text-white'
          }
          data-testid={isLooking ? 'leave-lfs-button' : 'find-players-button'}
        >
          {isLooking ? (
            <>
              <X size={14} className="mr-1" />
              Stop Looking
            </>
          ) : (
            <>
              <UserPlus size={14} className="mr-1" weight="bold" />
              Find Players
            </>
          )}
        </Button>
      </div>

      {disabled && (
        <p
          className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2 mb-3"
          data-testid="lfs-verify-required"
        >
          Verify your email to find and invite other players.
        </p>
      )}

      <div className="space-y-2 min-h-[80px]" data-testid="lfs-list">
        {players.length === 0 ? (
          <p className="text-sm text-[#71717A] text-center py-4">
            {isLooking
              ? 'Waiting for others… you&apos;re the first one here!'
              : 'No one is looking right now. Be the first to Find Players.'}
          </p>
        ) : (
          players.map((p) => {
            const isFav = friendIds.has(p.user_id);
            return (
              <div
                key={p.user_id}
                className="flex items-center justify-between gap-3 bg-[#FAFAFA] border border-[#E4E4E7] rounded px-3 py-2"
                data-testid={`lfs-row-${p.user_id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar className="w-7 h-7">
                    <AvatarImage src={p.avatar} alt={p.username} />
                    <AvatarFallback>{p.username?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-[#09090B] truncate">
                    {p.username}
                  </span>
                </div>
                <div className="flex gap-1">
                  <Button
                    onClick={() => toggleFavourite(p)}
                    disabled={savingFavourite === p.user_id || disabled}
                    size="sm"
                    variant="ghost"
                    className={`h-8 px-2 ${
                      isFav
                        ? 'text-[#FF3366] hover:bg-[#FFE5EB]'
                        : 'text-[#71717A] hover:bg-[#F4F4F5]'
                    }`}
                    data-testid={`favourite-toggle-${p.user_id}`}
                    aria-label={isFav ? 'Remove from favourites' : 'Save as favourite'}
                    title={isFav ? 'Remove from favourites' : 'Save as favourite'}
                  >
                    <Heart size={14} weight={isFav ? 'fill' : 'regular'} />
                  </Button>
                  <Button
                    onClick={() => sendInvite(p)}
                    disabled={sendingInviteTo === p.user_id || disabled}
                    size="sm"
                    variant="outline"
                    className="border-[#0047FF] text-[#0047FF] hover:bg-[#0047FF] hover:text-white transition-colors"
                    data-testid={`invite-button-${p.user_id}`}
                  >
                    {sendingInviteTo === p.user_id ? 'Sending…' : 'Invite'}
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
